// Modules
import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, FieldArray, formValueSelector, Field } from 'redux-form';
import _ from 'lodash';
import { connect } from 'react-redux';

// Components
import { FixedTable } from 'components/content-containers/tables';
import PurchaseMetadata from '../purchase-metadata';
import LineItemRows from './components/purchase-order-line-item-rows';
import { TotalRow, SubmitRow } from './components/shared-form-components';
import { AddButton } from 'components/widgets/buttons';
import Tooltip from 'components/widgets/overlays/tooltip';
import { TextInput, DateInput, CheckboxInput } from 'components/forms/form-elements';
import CompanySelect from 'components/forms/selects/company-select';
import TicketSelect from 'components/forms/selects/ticket-select';
import UserSelect from 'components/forms/selects/user-select';
import VendorSelect from 'components/forms/selects/vendor-select';

// Helpers
import requiredValidation from 'utils/required-validation';


// Component
class PurchaseOrderForm extends React.Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    header: PropTypes.node,
    disableLineItems: PropTypes.bool,
    autoFocusField: PropTypes.string,
    array: PropTypes.object,
    form: PropTypes.string,
    change: PropTypes.func,
    formValues: PropTypes.object
  };

  componentWillReceiveProps(nextProps) {
    const { lineItems } = this.props.formValues;
    const { lineItems: nextLineItems } = nextProps.formValues;

    // Add a new line item when the last line item changes (as long as the change wasn't to add or remove a line item)
    if (lineItems.length === nextLineItems.length && !_.isEqual(_.last(lineItems), _.last(nextLineItems))) {
      this.props.array.push('lineItems', {});
    }
  }

  render() {
    const {
      handleSubmit,
      disableLineItems,
      autoFocusField,
      array,
      change,
      formValues: { lineItems, company },
      ...props
    } = this.props;
    
    const activeLineItems = _.reject(lineItems, (lineItem) => lineItem.status === 'Archived');
    const lineItemTotals = _.map(activeLineItems, (lineItem) => (lineItem.quantity * lineItem.unitCost) || 0);
    const total = _.sum(lineItemTotals);

    return (
      <form onSubmit={handleSubmit}>
        <PurchaseMetadata
          className="form-metadata"
          company={<Field component={CompanySelect} name="company" autoFocus={autoFocusField === 'company'} />}
          ticket={<Field component={TicketSelect} name="ticket" company={company} />}
          date={<Field component={DateInput} name="date" />}
          purchasedBy={<Field component={UserSelect} name="purchasedBy" />}
          vendor={<Field component={VendorSelect} name="vendor" autoFocus={autoFocusField === 'vendor'} />}
          approvedBy={<Field component={TextInput} name="approvedBy" />}
          replenishment={<Field component={CheckboxInput} name="replenishment" />}
        />
        <FixedTable
          className="form-table"
          invisibleHeaders={[
            'col-purchase-order-spacing',
            'col-quantity-input',
            'col-money-input',
            'col-money',
            '',
            'col-icon-button'
          ]}
          headerRow={
            <tr>
              <th colSpan="5">Line Items</th>
              <th className="add-line-item-cell">
                <Tooltip title={disableLineItems ? 'Can\'t add line items after a charge has been added' : ''}>
                  <AddButton onClick={() => array.push('lineItems', {})} disabled={disableLineItems} />
                </Tooltip>
              </th>
            </tr>
          }
        >
          { disableLineItems &&
            <tbody>
              <tr>
                <td colSpan="6">
                  <div className="success-block notification-block">Great, we love keeping things organized! Changing line items after a charge has entered can be dangerous from an accounting perspective, so please talk to a team lead or department head to get this done!</div>
                </td>
              </tr>
            </tbody>
          }
          <FieldArray name="lineItems" component={LineItemRows} disableLineItems={disableLineItems} lineItems={lineItems} change={change} />
          <tbody>
            <TotalRow total={total}>
              Subtotal <span className="deemphasize">(Tax and shipping not included)</span>
            </TotalRow>
            <SubmitRow {...props} />
          </tbody>
        </FixedTable>
      </form>
    );
  }
}

const ConnectedForm = connect((state, ownProps) => ({
  formValues: formValueSelector(ownProps.form)(state, 'lineItems', 'company')
}))(PurchaseOrderForm);

export default reduxForm({ validate: purchaseValidation })(ConnectedForm);


// Purchase validation
function purchaseValidation(values) {
  const errors = requiredValidation(['company', 'ticket', 'vendor', 'date', 'purchasedBy', 'approvedBy'], values);
  const unarchivedLineItems = _.reject(values.lineItems, { status: 'Archived' });

  errors.lineItems = _.map(values.lineItems, (lineItem, index) => {
    // If there are two or more unarchived line items, this line item is the last line item,
    // and this line item is empty don't validate this line item
    if (unarchivedLineItems.length > 1 && index === values.lineItems.length - 1 && _.every(lineItem, _.isEmpty)) {
      return {};
    }

    // If this line item has been archived, don't validate it
    if (lineItem.status === 'Archived') return {};

    // Require line item fields
    const lineItemErrors = requiredValidation(['quantity', 'description', 'unitCost'], lineItem);
    // Require a quantity greater than zero
    if (!lineItemErrors.quantity && lineItem.quantity <= 0) lineItemErrors.quantity = 'Too low';

    return lineItemErrors;
  });

  // Redux form will think the form is invalid if you return empty line item error objects
  if (_.every(errors.lineItems, _.isEmpty)) delete errors.lineItems;
  
  return errors;
}
